import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useAmplitude } from 'react-amplitude-hooks'

import {
  EnrolState,
  CourseType,
  MonitoringStatus,
  CourseTypeCard,
  SynchronousCourseTypes,
  AmplitudeEvents,
} from 'enum'
import { IMainState } from 'store/states'
import { ICourseCard } from '../../../../models/courseCard.model'
import { ICardEnrol } from '../types'

interface UseAmplitudeActionsProps {
  course: ICourseCard
  enrolled: boolean
  enrol: ICardEnrol | null
}

export const useAmplitudeActions = ({
  course,
  enrolled,
  enrol,
}: UseAmplitudeActionsProps) => {
  const { logEvent } = useAmplitude()

  const { permission } = useSelector(({ user }: IMainState) => user)
  const { myList, section } = useSelector(({ courses }: IMainState) => courses)

  const buttonMessage = useCallback(() => {
    if (!enrolled && permission && permission.admin)
      return CourseTypeCard.explore
    if (!enrolled) {
      if (
        course.courseType === CourseType.Closed &&
        enrol?.stateAdmin === EnrolState.Pending
      )
        return CourseTypeCard.pending
      return CourseTypeCard.explore
    } else {
      switch (enrol?.advance) {
        case MonitoringStatus.Finish:
          return CourseTypeCard.review
        case MonitoringStatus.Pending:
          return CourseTypeCard.start
        default:
          return CourseTypeCard.continue
      }
    }
  }, [
    enrol?.advance,
    course.courseType,
    enrol?.stateAdmin,
    enrolled,
    permission,
  ])

  const amplitudeActions = useCallback(() => {
    const type = buttonMessage()
    const synchronousCourse = course?.synchronousCourse
    const isExternal =
      synchronousCourse?.synchronousCourseType ===
      SynchronousCourseTypes.external
    const actions = {
      [CourseTypeCard.explore]: () => {
        logEvent(AmplitudeEvents.lmsCourseViewed, {
          source: 'module',
          'Course ID': course.id,
          'Type of Platform': '',
          'Requires Application': !!course.mandatory,
          Sincronicity: isExternal,
          'Number of Courses finished': myList.filter(
            (course) => course.advance === MonitoringStatus.Finish
          ).length,
          'Course Result': !!enrol?.approved,
          Section: section,
        })
      },
      [CourseTypeCard.review]: () => {
        logEvent(AmplitudeEvents.lmsCourseReviewed, {
          source: 'module',
          'Course ID': course.id,
          'Type of Platform': '',
          'Requires Application': !!course.mandatory,
          Sincronicity: isExternal,
          'Number of Courses reviewed': myList.filter(
            (course) => course.advance === MonitoringStatus.Finish
          ).length,
          'Course Result': !!enrol?.approved,
          Section: section,
        })
      },
      [CourseTypeCard.start]: () => {
        logEvent(AmplitudeEvents.lmsCourseStarted, {
          source: JSON.stringify(origin) !== '{}' ? 'widget' : 'module',
          'Course ID': course.id,
          'Requires Application': !!course.mandatory,
          Sincronicity: isExternal,
          'Number of Courses started': myList.filter(
            (course) => course.advance === MonitoringStatus.Pending
          ).length,
          Section: section,
        })
      },
    }

    actions[type]?.()
  }, [
    buttonMessage,
    course.id,
    course.mandatory,
    course?.synchronousCourse,
    enrol?.approved,
    logEvent,
    myList,
    section,
  ])

  return amplitudeActions
}
